export const en = {
  app: {
    name: 'Chess',
  },
  seo: {
    title:
      'Play Chess Online Free — vs Bot or a Friend | BN-games',
    description:
      'Play chess free in your browser — against a bot with four levels, with a friend on one device, or online by sharing a room code. Optional clock, PGN export. No sign-up.',
    social:
      'Chess in your browser. Play a bot, a friend on one device, or anyone online with a room code.',
    h1:
      'Play chess online — against a bot or a friend',
    intro:
      'Chess is played by two players on an 8×8 board, each starting with sixteen pieces: a king, a queen, two rooks, two bishops, two knights and eight pawns. The goal is to checkmate the opponent\'s king — attack it so that it has no way to escape.\n\nBN-games plays by the standard FIDE rules, including castling, en passant, promotion and every draw rule. Play free in your browser against a bot, with a friend on the same device, or online by sending a room code — with or without a clock.',
    rulesTitle:
      'Chess Rules — How Every Piece Moves, Castling, Checkmate | BN-games',
    rulesDescription:
      'How to play chess: the board, how each piece moves, castling, en passant, promotion, check and checkmate, and every way a game can end in a draw — then play free online.',
    rulesH1:
      'Chess rules: how to play',
    playCta:
      'Play chess now',
  },
  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    google: 'Google',
    github: 'GitHub',
    completeProfile: 'Complete your profile →',
    profile: 'Profile',
    backToLanding: '← BN-games',
  },
  social: {
    close: 'Close',
    invite: {
      message: '{{sender}} invites you to {{game}}',
      moreCount_one: '+{{count}} more invite',
      moreCount_few: '+{{count}} more invites',
      moreCount_many: '+{{count}} more invites',
      moreCount_other: '+{{count}} more invites',
      accept: 'Accept',
      decline: 'Decline',
      acceptAria: "Accept {{sender}}'s {{game}} invite",
      declineAria: "Decline {{sender}}'s {{game}} invite",
    },
    dock: {
      pillLabel: 'From {{sender}}',
      pillAria: 'Invite from {{sender}} to {{game}} — show',
      popoverLabel: 'Invite',
      close: 'Close invite',
      leaveGameTitle: 'Leave this game?',
      leaveGameBody: 'Accepting opens {{game}} and leaves the current game. In an online game your seat is held for a while, so you can come back.',
      leaveGameConfirm: 'Leave and join',
      leaveGameCancel: 'Stay',
    },
    friendPicker: {
      button: 'Invite friend',
      modalTitle: 'Invite a friend',
      loading: 'Loading friends…',
      signInPrompt: 'Sign in to invite a friend.',
      empty: "You don't have any friends yet.",
      findFriends: 'Find friends',
      invite: 'Invite',
      inviteAria: 'Invite {{name}}',
      invited: 'Invited',
      invitedAria: 'Invited {{name}}',
      notAccepting: "This player isn't accepting invites right now",
      retryIn: 'Try again in {{seconds}}s',
    },
    addOpponent: {
      buttonMany: 'Add friends…',
      modalTitle: 'Add friends',
      add: 'Add {{name}} as friend',
      added: 'Added',
      addedAria: 'Added {{name}} as friend',
    },
  },
  mainMenu: {
    quickStart: 'Quick Start',
    newGame: 'New Game',
    rules: 'Rules',
    settings: 'Settings',
    history: 'Games History',
    about: 'About',
  },
  online: {
    create: {
      title: 'Create room',
      sideLabel: 'Your side',
      clockLabel: 'Time control',
      noClock: 'No clock',
      codeLabel: 'Room code',
      share: 'Share this code with your opponent',
      clockInfo: 'Time control: {{clock}}',
      create: 'Create room',
    },
    share: {
      copyCode: 'Copy code',
      copyLink: 'Copy link',
      copied: 'Copied!',
    },
    join: {
      title: 'Join room',
      codeLabel: 'Enter the room code',
      typeHint: 'Type the {{count}}-character code',
      enterHint: 'Press Enter to join',
      submit: 'Join',
      paste: 'Paste',
    },
    errors: {
      roomNotFound: 'There is no room with this code.',
      roomFull: 'This room is already full.',
      roomExpired: 'This room has closed.',
      connectionLost: 'Connection lost. Check your network and try again.',
    },
    status: {
      connecting: 'Connecting',
      creating: 'Creating room',
      waitingForOpponent: 'Waiting for opponent',
      opponentJoined: 'Opponent joined',
      joining: 'Joining',
      couldNotJoin: 'Could not join',
      error: 'Connection error',
    },
    banner: {
      opponentAway: '{{name}} disconnected',
    },
    draw: {
      pending: 'Draw offered…',
      declined: 'Draw offer declined',
      offeredTitle: 'Draw offered',
      offeredBody: '{{name}} offers a draw.',
    },
    restart: {
      title: 'Restart proposed',
      body: '{{name}} proposes starting this game over.',
      accept: 'Restart',
    },
    overlay: {
      reconnecting: 'Reconnecting',
      restoringGame: 'Restoring game {{countdown}}',
      connectionLost: 'Connection lost',
      gameEnded: 'The game has ended',
      rematchOffered: 'Rematch offered',
      waitingForOpponent: 'Waiting for {{name}}',
      rematchRequested: '{{name}} wants a rematch',
      rematchDeclined: '{{name}} declined the rematch',
      buttons: {
        leave: 'Leave',
        rematch: 'Rematch',
        returnToMenu: 'Menu',
        cancel: 'Cancel',
        accept: 'Accept',
        decline: 'Decline',
      },
    },
    resumeModal: {
      reconnectingTitle: 'Reconnecting…',
      reconnectingBody: 'Reconnecting to your game…',
      seatTakenTitle: 'Seat taken',
      seatTakenBody: 'That seat belongs to another account. Sign in with that account, or start a new game.',
      roomGoneTitle: 'Table closed',
      roomGoneBody: 'This game is over — the table has closed.',
      roomFullTitle: 'Room full',
      roomFullBody: 'Every seat at this table is taken, and none of them is yours.',
      anonymousTitle: "We can't confirm this seat is yours",
      anonymousBody: 'This browser has no record of the game. Sign in before your next online game and your seat follows you to any device.',
      unreachableTitle: "Couldn't reach the server",
      unreachableBody: "The server didn't answer. Check your connection and try again.",
      signIn: 'Sign in',
      viewSaved: 'View saved position',
      retry: 'Try again',
      discard: 'Discard',
    },
  },
  historyList: {
    title: 'Game history',
    empty: 'No saved games yet — play to start filling this list.',
    vsTemplate: '{{white}} vs {{black}}',
    winnerTemplate: '{{name}} wins',
    draw: 'Draw',
    movesCount_one: '{{count}} move',
    movesCount_few: '{{count}} moves',
    movesCount_many: '{{count}} moves',
    movesCount_other: '{{count}} moves',
    deleteAria: 'Delete this game',
    deleteConfirm: 'Delete this saved game?',
    botName: 'Bot ({{level}})',
    modes: {
      bot: 'vs Bot',
      local: 'Local',
      online: 'Online',
    },
    accountSection: {
      title: 'Your account',
      loading: 'Loading…',
      empty: 'No online games recorded yet.',
      statsLine: '{{wins}}W – {{losses}}L – {{draws}}D ({{played}} played)',
    },
    deviceSection: {
      title: 'On this device',
    },
    unreadableTitle: 'This game cannot be opened',
    unreadableBody: 'The saved record does not replay, so there is nothing to show.',
  },
  moveList: {
    title: 'Moves',
    empty: 'No moves yet',
    expand: 'Show all moves',
    collapse: 'Show one line of moves',
    goTo: 'Go to {{move}}',
    materialEven: 'Material: even',
    materialLead: 'Material: {{side}} +{{points}}',
    captures: 'Captures: {{white}} – {{black}}',
  },
  exportGame: {
    label: 'Export this game',
    copyPgn: 'Copy PGN',
    downloadPgn: 'Download PGN',
    copyFen: 'Copy FEN',
    copied: 'Copied!',
  },
  importPosition: {
    title: 'Import position',
    hint: 'Paste a FEN position or a PGN game. A PGN continues from its last move.',
    inputLabel: 'FEN or PGN',
    paste: 'Paste',
    playBot: 'Play vs Bot',
    playHuman: 'Play vs Human',
    readyWhite: 'Ready — White to move, move {{move}}.',
    readyBlack: 'Ready — Black to move, move {{move}}.',
    errors: {
      empty: 'Paste a FEN or a PGN first.',
      fen: 'This is not a valid FEN position.',
      pgn: 'This PGN could not be read.',
      over: 'This game is already over — there is nothing left to play.',
    },
  },
  replay: {
    controls: {
      first: 'First',
      previous: 'Previous',
      play: 'Play',
      pause: 'Pause',
      next: 'Next',
      last: 'Last',
      speed: 'Speed',
      backToHistory: 'Back to history',
      backToMenu: 'Back to menu',
    },
  },
  common: {
    back: '← Back',
    opponent: 'Opponent',
    white: 'White',
    black: 'Black',
    cycle: 'press Enter to cycle',
  },
  gameOverlay: {
    title: 'Game paused',
    settingsLabel: 'Settings',
    settingsBackLabel: '← Back',
    settingsViewTitle: 'Settings',
    restartLabel: 'Restart match',
    restartHintOnline: 'Online: all seats must agree. You get one proposal per match.',
    restartHintAlreadyProposed: "You've used your restart proposal this match.",
    restartHintPending: 'Waiting on the other seats…',
    returnToMenuLabel: 'Return to main menu',
    returnToMenuConfirmTitle: 'Leave this match and return to the menu?',
    returnToMenuConfirmYes: 'Yes, leave',
    returnToMenuConfirmNo: 'Stay',
    giveUpLabel: 'Give up',
    giveUpConfirmTitle: "Are you sure you're a coward?",
    giveUpConfirmYes: "Yes, I'm a coward",
    giveUpConfirmNo: "I'll fight on",
    settingsTabs: {
      gameplay: 'Gameplay',
      interface: 'Interface',
      audio: 'Audio',
    },
    discardConfirmTitle: 'Discard current game?',
    discardConfirmBody: 'Changing this setting will end the current game and start a new one.',
    discardConfirmYes: 'Discard and restart',
    discardConfirmNo: 'Cancel',
    close: 'Close',
  },
  playMenu: {
    title: 'Play',
    vsAi: 'vs Bot',
    vsHuman: 'vs Human (local)',
    createOnline: 'Create online game',
    joinOnline: 'Join online game',
    importPosition: 'Import position (FEN / PGN)',
    resumeOnline: 'Resume online ({{code}})',
    resumeOnlineVs: 'Resume online · vs {{opponent}}',
  },
  settings: {
    title: 'Settings',
    tabs: {
      gameplay: 'Gameplay',
      interface: 'Interface',
      audio: 'Audio',
    },
    rows: {
      playerName: 'Player name',
      sound: 'Sound',
      difficulty: 'Difficulty',
      defaultColor: 'Default color',
      theme: 'Theme',
      language: 'Language',
      showValidMoves: 'Show valid moves',
      showMoveList: 'Show move list',
      afkAssistEnabled: 'AFK move hint',
      afkRemindersEnabled: 'AFK reminder sound',
      turnStartSound: 'Your-turn sound',
      clockEnabled: 'Chess clock',
      clockPreset: 'Time control',
      flipBoard: 'Flip board after each move (2 players)',
      showAfkPingButton: 'Show ping button',
    },
    difficulties: {
      easy: 'Easy',
      normal: 'Normal',
      hard: 'Hard',
      expert: 'Expert',
    },
    colors: {
      white: 'White',
      black: 'Black',
      random: 'Random',
    },
    clockPresets: {
      bullet: 'Bullet · 1 min',
      blitz: 'Blitz · 5 min',
      rapid: 'Rapid · 10 min',
    },
    themes: {
      mahogany: 'Mahogany',
      persian: 'Persian',
      deco: 'Deco',
      monte: 'Monte',
    },
    languages: {
      en: 'English',
      ru: 'Русский',
    },
    on: 'On',
    off: 'Off',
    namePrompt: 'Enter your name:',
  },
  about: {
    version: 'Version {{version}}',
    tech: 'TypeScript + Canvas',
    body: 'Standard chess by FIDE rules, against a bot or a friend.',
  },
  game: {
    banner: {
      toMove: '{{side}} to move',
      toMoveCheck: '{{side}} to move — check!',
      yourMove: 'Your move',
      yourMoveCheck: 'Your move — check!',
      botThinking: 'Bot is thinking…',
      wins: '{{side}} wins',
      youWin: 'You win!',
      youLose: 'You lose',
      draw: 'Draw',
    },
    termination: {
      checkmate: 'Checkmate',
      stalemate: 'Stalemate',
      insufficient: 'Insufficient material',
      threefold: 'Threefold repetition',
      fiftyMove: '50-move rule',
      resign: 'Resignation',
      agreement: 'Draw agreed',
      flag: 'Time ran out',
    },
    draw: {
      offer: 'Offer draw',
      hotSeatTitle: 'Draw offered',
      hotSeatBody: '{{side}} offers a draw. Does {{other}} accept?',
      accept: 'Accept draw',
      decline: 'Keep playing',
      botDeclined: 'The bot declines your draw offer.',
    },
    gameOver: {
      win: 'You win!',
      loss: 'You lose',
      draw: 'Draw',
      sideWins: '{{side}} wins',
      playAgain: 'Play again',
      menu: 'Main menu',
    },
    pingOpponent: 'Ping opponent',
    tooltips: {
      pause: 'Pause menu',
      hideMoveList: 'Hide move list',
      showMoveList: 'Show move list',
    },
  },
  promotion: {
    title: 'Promote pawn to',
    queen: 'Queen',
    rook: 'Rook',
    bishop: 'Bishop',
    knight: 'Knight',
    cancel: 'Cancel',
  },
  a11y: {
    canvasLabel: 'Chess board. Use the arrow keys to move across squares, Enter to pick up a piece and Enter again to move it.',
  },
  narration: {
    sides: {
      white: 'White',
      black: 'Black',
    },
    pieces: {
      pawn: 'pawn',
      knight: 'knight',
      bishop: 'bishop',
      rook: 'rook',
      queen: 'queen',
      king: 'king',
    },
    piecesTaken: {
      pawn: 'pawn',
      knight: 'knight',
      bishop: 'bishop',
      rook: 'rook',
      queen: 'queen',
      king: 'king',
    },
    move: '{{side}}: {{piece}} from {{from}} to {{to}}.',
    capture: '{{side}}: {{piece}} from {{from}} takes {{captured}} on {{to}}.',
    castleKing: '{{side}} castles kingside.',
    castleQueen: '{{side}} castles queenside.',
    promotion: 'Promotes to {{piece}}.',
    check: 'Check.',
    gameOver: '{{result}}. {{reason}}.',
    cursor: {
      empty: '{{square}}',
      piece: '{{square}}, {{side}} {{piece}}',
      legal: '{{square}}, legal move',
      capture: '{{square}}, capture {{piece}}',
    },
    selected: 'Selected {{piece}} on {{square}}.',
    deselected: 'Selection cleared.',
  },
  rules: {
    screenTitle: 'Rules',
    fide: {
      title: 'Standard chess (FIDE rules)',
      items: {
        setup: {
          title: 'Setup',
          body: 'The board has 64 squares and is placed so that each player has a light square in the right-hand corner. Rooks go in the corners, then knights, then bishops; the queen stands on her own colour (white queen on d1, black queen on d8) and the king next to her. Each side\'s eight pawns fill the row in front. White moves first, and the players then alternate one move at a time.',
        },
        king: {
          title: 'King',
          body: 'The king moves one square in any direction. It may never move onto a square attacked by an enemy piece.',
        },
        queen: {
          title: 'Queen',
          body: 'The queen moves any number of squares in a straight line — along a row, a column or a diagonal — but cannot jump over other pieces.',
        },
        rook: {
          title: 'Rook',
          body: 'The rook moves any number of squares along a row or a column, without jumping.',
        },
        bishop: {
          title: 'Bishop',
          body: 'The bishop moves any number of squares diagonally, without jumping, so it stays on squares of one colour for the whole game.',
        },
        knight: {
          title: 'Knight',
          body: 'The knight moves in an L shape: two squares in one direction and then one square sideways. It is the only piece that jumps over others.',
        },
        pawn: {
          title: 'Pawn',
          body: 'A pawn moves straight forward one square, or two squares from its starting row if both are empty. It captures differently: one square diagonally forward. Pawns never move backwards.',
        },
        capture: {
          title: 'Capturing',
          body: 'A piece captures by moving onto a square occupied by an enemy piece, which is removed from the board. Capturing is never compulsory.',
        },
        castling: {
          title: 'Castling',
          body: 'Once per game, the king may move two squares towards a rook, and that rook jumps to the square the king crossed. It is allowed only if neither piece has moved yet, the squares between them are empty, and the king is not in check, does not pass through an attacked square and does not land on one.',
        },
        enPassant: {
          title: 'En passant',
          body: 'When a pawn advances two squares and lands beside an enemy pawn, that enemy pawn may capture it as if it had moved only one square. The capture must be made immediately, on the very next move, or the right is lost.',
        },
        promotion: {
          title: 'Promotion',
          body: 'A pawn that reaches the far row must be exchanged for a queen, rook, bishop or knight of the same colour — the player\'s choice, regardless of which pieces are already on the board.',
        },
        check: {
          title: 'Check',
          body: 'A king that is attacked is in check. The player in check must get out of it at once — by moving the king, capturing the attacker, or blocking the line of attack. A move that leaves your own king in check is not allowed.',
        },
        checkmate: {
          title: 'Checkmate',
          body: 'If a king is in check and there is no move that gets it out, it is checkmate and that player loses. A player may also resign at any time.',
        },
        stalemate: {
          title: 'Stalemate',
          body: 'If the player to move is not in check but has no legal move at all, the game is a draw by stalemate.',
        },
        draws: {
          title: 'Other draws',
          body: 'The game is also drawn when neither side has enough material left to checkmate (for example king against king, or king and bishop against king), when the same position occurs three times with the same player to move, when fifty moves in a row by each side pass without a pawn move or a capture, or when both players agree to a draw.',
        },
        clock: {
          title: 'Playing with a clock',
          body: 'With the clock on, each player has a fixed amount of time for the whole game, and only the player to move has their clock running. A player whose time runs out loses — unless the opponent has no way left to checkmate, in which case the game is a draw.',
        },
      },
    },
  },
  pwa: {
    updateAvailable: {
      title: 'Update available',
      body: 'A new version of Chess is ready. Refresh to load it.',
      refresh: 'Refresh',
      later: 'Later',
    },
  },
};
